import { createTheme, responsiveFontSizes } from '@mui/material/styles';

const brandColors = {
  primaryText: '#1C1B1F',
  secondaryText: '#625B71',
  primaryBlue: '#294190',
  accentGreen: '#4F8506',
  successBg: '#EEF8DE',
  surface: '#FFFFFF',
  surfaceAlt: '#FEFEFE',
  mutedSurface: '#F7F7F9',
  border: '#E4E6F1',
  tableBorder: '#DFE5F6',
  danger: '#DA342E',
};

let appTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brandColors.primaryBlue,
    },
    secondary: {
      main: brandColors.accentGreen,
    },
    error: {
      main: brandColors.danger,
    },
    text: {
      primary: brandColors.primaryText,
      secondary: brandColors.secondaryText,
    },
    background: {
      default: brandColors.surfaceAlt,
      paper: brandColors.surface,
    },
    divider: brandColors.border,
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontFamily: 'Montserrat, Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: '34px',
      lineHeight: 1.1765,
      letterSpacing: '-0.25px',
      fontWeight: 700,
      color: brandColors.primaryText,
    },
    h2: {
      fontFamily: 'Montserrat, Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: '30px',
      lineHeight: 1.2,
      letterSpacing: '-0.25px',
      fontWeight: 700,
      color: brandColors.primaryText,
    },
    h3: {
      fontSize: '20px',
      lineHeight: 1.4,
      fontWeight: 700,
      color: brandColors.primaryText,
    },
    subtitle1: {
      fontSize: '16px',
      lineHeight: 1.5,
      letterSpacing: '0.15px',
    },
    subtitle2: {
      fontSize: '14px',
      lineHeight: 1.4286,
      letterSpacing: '0.1px',
      fontWeight: 500,
    },
    body1: {
      fontSize: '16px',
      lineHeight: 1.5,
      letterSpacing: '0.15px',
      color: brandColors.primaryText,
    },
    body2: {
      fontSize: '14px',
      lineHeight: 1.4286,
      letterSpacing: '0.1px',
      color: brandColors.primaryText,
    },
    caption: {
      fontSize: '12px',
      lineHeight: 1.3334,
      letterSpacing: '0.4px',
      color: brandColors.secondaryText,
    },
    button: {
      textTransform: 'none',
      fontSize: '16px',
      lineHeight: 1.5,
      letterSpacing: '0.15px',
      fontWeight: 500,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif',
          backgroundColor: brandColors.surfaceAlt,
          color: brandColors.primaryText,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
        },
        '*, *::before, *::after': {
          boxSizing: 'border-box',
        },
        '.MuiGrid-root, .MuiStack-root, .MuiPaper-root': {
          minWidth: 0,
          maxWidth: '100%',
        },
        '.MuiGrid-container': {
          maxWidth: '100%',
        },
        '#root': {
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
        },
        '.app-page-title': {
          fontFamily: 'Montserrat, Roboto, "Helvetica Neue", Arial, sans-serif',
        },
        '.table-scroll-host::-webkit-scrollbar': {
          display: 'block',
          height: '12px',
        },
        '.table-scroll-host::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '.table-scroll-host::-webkit-scrollbar-thumb': {
          background: '#C7D2F0',
          borderRadius: '999px',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
        contained: {
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 16,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: brandColors.primaryText,
          fontSize: '14px',
          lineHeight: '16px',
          letterSpacing: '0.4px',
          fontWeight: 500,
          '&.Mui-focused': {
            color: brandColors.primaryText,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: brandColors.primaryText,
          fontSize: '14px',
          lineHeight: '16px',
          letterSpacing: '0.4px',
          fontWeight: 500,
          '&.Mui-focused': {
            color: brandColors.primaryText,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginTop: 8,
          fontSize: '12px',
          lineHeight: '16px',
          letterSpacing: '0.4px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: brandColors.surface,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: brandColors.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: brandColors.border,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: brandColors.primaryBlue,
          },
          '&.Mui-disabled': {
            backgroundColor: brandColors.mutedSurface,
          },
        },
        input: {
          fontSize: '16px',
          lineHeight: '24px',
          letterSpacing: '0.5px',
          fontWeight: 400,
          color: brandColors.primaryText,
          '&::placeholder': {
            color: brandColors.secondaryText,
            opacity: 1,
            fontWeight: 400,
          },
          '&.Mui-disabled': {
            WebkitTextFillColor: '#8C8B94',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: '16px',
          lineHeight: '24px',
          letterSpacing: '0.5px',
          fontWeight: 400,
          color: brandColors.primaryText,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: '16px',
          lineHeight: '24px',
          letterSpacing: '0.5px',
          fontWeight: 400,
          color: brandColors.primaryText,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 8,
          borderRadius: 12,
          border: `1px solid ${brandColors.border}`,
          boxShadow: '0px 8px 24px rgba(31, 46, 85, 0.08)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 44,
          fontSize: '16px',
          lineHeight: '24px',
          letterSpacing: '0.5px',
          fontWeight: 400,
          color: brandColors.primaryText,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 24,
          border: `1px solid ${brandColors.tableBorder}`,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          backgroundColor: brandColors.surface,
        },
        columnHeaders: {
          backgroundColor: brandColors.tableBorder,
        },
        columnHeader: {
          padding: 0,
        },
        columnHeaderTitle: {
          fontWeight: 700,
        },
        columnHeaderTitleContainer: {
          paddingLeft: 7,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

appTheme = responsiveFontSizes(appTheme, {
  breakpoints: ['xs', 'sm', 'md', 'lg', 'xl'],
  factor: 2,
});

export default appTheme;
